/**
 * MapComponent
 *
 * Generic interactive map component for visualizing GPS sensor data
 * with multiple layer types using MapLibre GL JS.
 *
 * Created: January 2026
 * Phase 1: Core Map Component Implementation
 * Phase 3: Advanced Map Layers (Marker, Cluster, Zone Overlays)
 * Phase 4: Real-Time Data Updates (WebSocket Integration)
 * Phase 5: Map Controls & Legend Implementation
 * Phase 7: Responsive & Accessibility Enhancements
 *
 * Features:
 * - Heatmap visualization with agricultural color scheme
 * - Marker layer for individual sensors with color-coded values
 * - Cluster layer for grouping nearby sensors (Supercluster)
 * - Zone overlay layer for field polygon visualization
 * - Query Template data binding support
 * - Responsive container sizing
 * - Custom navigation controls (zoom, compass, fit bounds, fullscreen)
 * - Interactive legend with layer toggles
 * - Scale indicator
 * - Smooth zoom-based layer transitions
 * - Real-time WebSocket updates with pulse animations
 * - Connection status indicator
 * - Offline/stale sensor detection
 *
 * Phase 7 Responsive Design:
 * - Mobile (≤599px): Collapsible controls, compact legend
 * - Tablet (600-1023px): Standard compact mode
 * - Desktop (≥1024px): Full size controls and legend
 * - Touch gestures: Pinch zoom, pan via react-map-gl
 *
 * Phase 7 Accessibility (WCAG 2.1 AA):
 * - Full keyboard navigation (+/- for zoom, N for north, F for fit)
 * - ARIA labels for all controls
 * - Screen reader announcements via aria-live regions
 * - High contrast text (minimum 4.5:1 ratio)
 * - Focus indicators
 */

import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import MapGL, {
  Source,
  Layer,
  Popup,
} from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import type { CircleLayerSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { AlertCircle, MapPin, Layers, ExternalLink } from 'lucide-react';
import { ChartLoadingSkeleton } from '@/components/common/DataLoadingSkeletons';

import { Page } from '@/types';
import { ChartDataSource } from '@/types/chartDataSource';
import { useMapSensorData } from '@/hooks/useMapSensorData';
import { useEditorStore } from '@/stores/editor/editor-store';
import { getMapTilerApiKey } from '@/services/api/configApi';
import { useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import { useMapPinNavigation } from '@/hooks/useMapPinNavigation';

import {
  MapComponentProps,
  MapComponentProperties,
  SensorDataPoint,
  SensorTooltipData,
  MapLayerConfig,
  MapControlsConfig,
  MapLegendConfig,
  ZoneDataPoint,
  ZoneGeoJSON,
  ClusterPoint,
  MapGroup,
  MapGroupType,
  MapGroupGeoJSON,
  MapPin as MapPinType,
  MapPinGeoJSON,
  PinNavigationConfig,
  PinDataBinding,
  DEFAULT_MAP_VIEW,
  DEFAULT_LAYER_CONFIG,
  DEFAULT_SENSOR_DATA,
  DEFAULT_REALTIME_CONFIG,
  DEFAULT_CONTROLS_CONFIG,
  DEFAULT_LEGEND_CONFIG,
  DEFAULT_GROUP_COLORS,
} from './MapComponent.types';

import { useDataTextValue } from '@/hooks/useDataTextValue';

// Import map controls (Phase 5 & Phase 2: Groups & Phase 4: Pin Placement)
import { MapNavigationControls, MapScaleIndicator, MapLegend, PinPlacementOverlay } from './mapControls';
import { PinPropertiesPanel } from './mapControls/PinPropertiesPanel';

// Import pin placement hook
import { useMapPinPlacement } from '@/hooks/editor/useMapPinPlacement';

// Import layer modules
import {
  getHeatmapPaint,
  HEATMAP_LAYER_ID,
} from './mapLayers/heatmapLayer';

import {
  getMarkerPaint,
  getMarkerLabelPaint,
  getMarkerLabelLayout,
  getMoistureStatus,
  MARKER_LAYER_ID,
  MARKER_LABEL_LAYER_ID,
} from './mapLayers/markerLayer';

import {
  createSupercluster,
  loadClusterData,
  getClusters,
  getClusterExpansionZoom,
  getClusterPaint,
  getClusterCountPaint,
  getClusterCountLayout,
  getUnclusteredPointPaint,
  CLUSTER_LAYER_ID,
  CLUSTER_COUNT_LAYER_ID,
  UNCLUSTERED_POINT_LAYER_ID,
} from './mapLayers/clusterLayer';

import {
  convertZonesToGeoJSON,
  calculateZoneMoisture,
  getZoneFillPaint,
  getZoneStrokePaint,
  getZoneLabelPaint,
  getZoneLabelLayout,
  DEFAULT_ZONE_DATA,
  ZONE_FILL_LAYER_ID,
  ZONE_STROKE_LAYER_ID,
  ZONE_LABEL_LAYER_ID,
  ZONE_SOURCE_ID,
} from './mapLayers/zoneLayer';

import {
  convertGroupsToGeoJSON,
  getGroupFillPaint,
  getGroupStrokePaint,
  filterSensorsByGroups,
  GROUP_AREA_SOURCE_ID,
  GROUP_AREA_FILL_LAYER_ID,
  GROUP_AREA_STROKE_LAYER_ID,
} from './mapLayers/groupLayer';

import {
  convertPinsToGeoJSON,
  mapPinIconToUtilName,
  getPinSymbolLayout,
  getPinSymbolPaint,
  getPinLabelLayout,
  getPinLabelPaint,
  getPinNavIndicatorLayout,
  getPinNavIndicatorPaint,
  PIN_SOURCE_ID,
  PIN_LAYER_ID,
  PIN_LABELS_LAYER_ID,
  PIN_NAV_INDICATOR_LAYER_ID,
} from './mapLayers/pinLayer';

import {
  preloadTeardropMarkers,
  normalizeColorForImageId,
  type PinIconName,
} from './mapUtils/iconUtils';

import {
  UPDATE_PULSE_SOURCE_ID,
  UPDATE_PULSE_LAYER_ID,
  OFFLINE_INDICATOR_LAYER_ID,
  getUpdatePulseLayerPaint,
  getOfflineIndicatorPaint,
  convertToPulseGeoJSON,
  convertToOfflineGeoJSON,
} from './mapLayers/updateIndicators';

import { isQueryTemplateDataSource } from '@/hooks/useQueryTemplateData';

// Import performance utilities (Phase 8)
import {
  cullSensorsByViewport,
  extractViewportBounds,
  calculateOptimalBuffer,
} from './mapPerformance/viewportCulling';
import {
  decimateSensors,
  getRecommendedDecimationSettings,
} from './mapPerformance/dataDecimation';
import {
  debounce,
  DEBOUNCE_DELAYS,
} from './mapPerformance/debounce';
import {
  usePerformanceMonitor,
  useFPSMonitor,
} from './mapPerformance/performanceMonitor';
import {
  useMemoizedGeoJSON,
  clearGeoJSONCache,
} from './mapPerformance/memoization';

// ============================================================================
// Pin Data Binding — Prefetcher + Display
//
// PinBindingFetcher: always mounted, eagerly fetches data and registers
//   refetch functions so the refresh button can trigger re-fetches.
// PinTooltipDataDisplay: pure display component reading from cache.
// ============================================================================

export interface PinDataCacheEntry {
  value: string | number | null;
  formattedValue: string;
  isLoading: boolean;
}

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isSensorDataPoint = (value: unknown): value is SensorDataPoint => {
  if (!isObjectRecord(value)) {
    return false;
  }

  const id = value.id;
  const deviceId = value.deviceId;
  const latitude = value.latitude;
  const longitude = value.longitude;
  const moistureLevel = value.moistureLevel;
  const timestamp = value.timestamp;

  return (
    typeof id === 'string' &&
    typeof deviceId === 'string' &&
    typeof latitude === 'number' &&
    Number.isFinite(latitude) &&
    typeof longitude === 'number' &&
    Number.isFinite(longitude) &&
    typeof moistureLevel === 'number' &&
    Number.isFinite(moistureLevel) &&
    (typeof timestamp === 'string' || timestamp instanceof Date)
  );
};

const isClusterPoint = (value: unknown): value is ClusterPoint => {
  if (!isObjectRecord(value)) {
    return false;
  }

  const geometry = value.geometry;
  const properties = value.properties;
  if (!isObjectRecord(geometry) || !isObjectRecord(properties)) {
    return false;
  }

  const coordinates = geometry.coordinates;
  return (
    value.type === 'Feature' &&
    geometry.type === 'Point' &&
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    typeof coordinates[0] === 'number' &&
    typeof coordinates[1] === 'number'
  );
};

type MapActionProperties = MapComponentProperties & {
  _action?: string;
  _actionTimestamp?: string;
  _groupName?: string;
  _groupType?: MapGroupType;
  _sensorType?: string;
  _visible?: boolean;
  _pinName?: string;
  _pageId?: string;
  zones?: ZoneDataPoint[];
};

const PinBindingFetcher: React.FC<{
  binding: PinDataBinding;
  componentId: string;
  onData: (bindingId: string, entry: PinDataCacheEntry) => void;
}> = ({ binding, componentId, onData }) => {
  const { formattedValue, isLoading, value } = useDataTextValue({
    dataSource: binding.dataSource,
    componentId: `${componentId}-pin-${binding.id}`,
    valueField: binding.valueField,
    valueFormatter: binding.valueFormatter || 'number',
    enabled: true,
    templateId: binding.queryTemplateId,
  });

  useEffect(() => {
    onData(binding.id, { value, formattedValue, isLoading });
  }, [value, formattedValue, isLoading, binding.id, onData]);

  return null;
};

const PinTooltipDataDisplay: React.FC<{
  binding: PinDataBinding;
  cached?: PinDataCacheEntry;
}> = ({ binding, cached }) => {
  let displayValue = cached?.formattedValue ?? '--';
  const rawValue = cached?.value;

  if (rawValue !== null && rawValue !== undefined && binding.decimalPlaces !== undefined && typeof rawValue === 'number') {
    displayValue = rawValue.toFixed(binding.decimalPlaces);
  } else if (rawValue !== null && rawValue !== undefined && binding.decimalPlaces !== undefined && !isNaN(Number(rawValue))) {
    displayValue = Number(rawValue).toFixed(binding.decimalPlaces);
  }
  if (binding.prefix) displayValue = `${binding.prefix}${displayValue}`;
  if (binding.suffix) displayValue = `${displayValue}${binding.suffix}`;
  else if (binding.unit) displayValue = `${displayValue} ${binding.unit}`;

  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="text-muted-foreground truncate">{binding.displayLabel}:</span>
      <span
        className="font-medium tabular-nums"
        style={{
          color: binding.color || undefined,
          fontSize: binding.fontSize || undefined,
          fontWeight: binding.fontWeight || undefined,
        }}
      >
        {cached?.isLoading ? '...' : displayValue}
      </span>
    </div>
  );
};

// Module-level viewport storage: persists across React remounts so that
// "re-lock" can capture the user's latest panned position even when the
// component unmounts/remounts between editor and viewer routes.
const liveViewports = new Map<string, {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
}>();

// ============================================================================
// Main Component
// ============================================================================

export const MapComponent: React.FC<MapComponentProps> = ({
  component,
  isPreview: _isPreview = false,
}) => {
  // ---------------------------------------------------------------------------
  // Props Destructuring with Defaults
  // ---------------------------------------------------------------------------
  const { properties = {}, style = {} } = component;
  const mapProperties = properties as MapComponentProperties;
  const actionProperties = mapProperties as MapActionProperties;
  const initialView = mapProperties.initialView;
  const initialViewCenter = initialView?.center;
  const initialViewCenterLongitude = initialViewCenter?.[0];
  const initialViewCenterLatitude = initialViewCenter?.[1];
  const initialViewZoom = initialView?.zoom;
  const initialViewPitch = initialView?.pitch;
  const initialViewBearing = initialView?.bearing;
  const onSensorClick = mapProperties.onSensorClick;

  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const updateComponent = useEditorStore(state => state.updateComponent);

  // ---------------------------------------------------------------------------
  // State Management
  // ---------------------------------------------------------------------------
  const [viewState, setViewState] = useState({
    longitude: mapProperties.initialView?.center?.[0] ?? DEFAULT_MAP_VIEW.center[0],
    latitude: mapProperties.initialView?.center?.[1] ?? DEFAULT_MAP_VIEW.center[1],
    zoom: mapProperties.initialView?.zoom ?? DEFAULT_MAP_VIEW.zoom,
    pitch: mapProperties.initialView?.pitch ?? 0,
    bearing: mapProperties.initialView?.bearing ?? 0,
  });

  const [hoveredSensor, setHoveredSensor] = useState<SensorTooltipData | null>(null);
  const [hoveredCluster, setHoveredCluster] = useState<ClusterPoint | null>(null);
  const [hoveredPin, setHoveredPin] = useState<{
    pin: MapPinType;
    longitude: number;
    latitude: number;
  } | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [markersLoaded, setMarkersLoaded] = useState(false);
  const [maptilerApiKey, setMaptilerApiKey] = useState<string | null>(null);
  const [apiKeyResolved, setApiKeyResolved] = useState(false);

  // Track programmatic moves to prevent onMoveEnd sync during AI commands
  const isProgrammaticMove = useRef(false);

  // Ref to safely read the latest viewState outside React's render cycle
  const viewStateRef = useRef(viewState);
  useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);

  // Track location lock state via ref to avoid stale closure issues in handleMoveEnd
  const isLocationLocked = useRef(mapProperties.locationLocked ?? false);

  // Keep locationLocked ref in sync with props
  useEffect(() => {
    isLocationLocked.current = mapProperties.locationLocked ?? false;
  }, [mapProperties.locationLocked]);

  // Fetch MapTiler API key from backend on mount
  useEffect(() => {
    getMapTilerApiKey().then((key) => {
      if (key) {
        setMaptilerApiKey(key);
      }
    }).catch(() => {
      // Proceed with fallback even if fetch fails
    }).finally(() => {
      setApiKeyResolved(true);
    });
  }, []);

  // Sync viewState when initialView props change (from AI commands)
  useEffect(() => {
    setViewState(prev => {
      const updated = { ...prev };
      let changed = false;

      if (initialViewZoom !== undefined && initialViewZoom !== prev.zoom) {
        updated.zoom = initialViewZoom;
        changed = true;
      }
      if (initialViewCenterLongitude !== undefined && initialViewCenterLongitude !== prev.longitude) {
        updated.longitude = initialViewCenterLongitude;
        changed = true;
      }
      if (initialViewCenterLatitude !== undefined && initialViewCenterLatitude !== prev.latitude) {
        updated.latitude = initialViewCenterLatitude;
        changed = true;
      }
      if (initialViewPitch !== undefined && initialViewPitch !== prev.pitch) {
        updated.pitch = initialViewPitch;
        changed = true;
      }
      if (initialViewBearing !== undefined && initialViewBearing !== prev.bearing) {
        updated.bearing = initialViewBearing;
        changed = true;
      }

      if (changed) {
        // Mark as programmatic move to prevent onMoveEnd from syncing back
        isProgrammaticMove.current = true;
        // Clear the flag after animation completes (500ms is typical transition time)
        setTimeout(() => {
          isProgrammaticMove.current = false;
        }, 600);
        return updated;
      }
      return prev;
    });
  }, [
    initialViewZoom,
    initialViewCenterLongitude,
    initialViewCenterLatitude,
    initialViewPitch,
    initialViewBearing,
  ]);

  // Track whether we've initialized center from data (ref declared here, effect runs later)
  const hasInitializedCenter = useRef(false);

  // Phase 5: Controls & Legend state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  // Local layer visibility state (for runtime toggling via legend)
  const [localLayerOverrides, setLocalLayerOverrides] = useState<Partial<{
    heatmap: { enabled: boolean };
    markers: { enabled: boolean };
    clusters: { enabled: boolean };
    zones: { enabled: boolean };
  }>>({});

  // Phase 2: Group visibility state (local overrides for runtime toggling)
  const [localGroupOverrides, setLocalGroupOverrides] = useState<Map<string, boolean>>(new Map());
  const lastGroupActionTimestamp = useRef<string | null>(null);
  const actionName = actionProperties._action;
  const actionTimestamp = actionProperties._actionTimestamp;
  const actionGroupName = actionProperties._groupName;
  const actionSensorType = actionProperties._sensorType;
  const actionGroupType = actionProperties._groupType;
  const actionVisible = actionProperties._visible;
  const actionPinName = actionProperties._pinName;
  const actionPageId = actionProperties._pageId;

  // Handle actions dispatched via AI commands (uses timestamp to detect new actions)
  useEffect(() => {
    if (!actionName || !actionTimestamp) return;
    if (actionTimestamp === lastGroupActionTimestamp.current) return;
    lastGroupActionTimestamp.current = actionTimestamp;

    // --- View actions: fitToBounds / refreshData ---
    if (actionName === 'fitToBounds' || actionName === 'refreshData') {
      const newView = mapProperties.initialView;
      if (newView) {
        isProgrammaticMove.current = true;
        setViewState(prev => ({
          ...prev,
          longitude: newView.center?.[0] ?? prev.longitude,
          latitude: newView.center?.[1] ?? prev.latitude,
          zoom: newView.zoom ?? prev.zoom,
          pitch: newView.pitch ?? prev.pitch,
          bearing: newView.bearing ?? prev.bearing,
        }));
        setTimeout(() => { isProgrammaticMove.current = false; }, 600);
      }
      return;
    }

    // --- Lock current view: capture live viewport into initialView ---
    if (actionName === 'lockCurrentView') {
      // Prefer the module-level liveViewports map which survives remounts,
      // falling back to viewStateRef for the first-lock case (no prior pan).
      const vs = liveViewports.get(component.id) ?? viewStateRef.current;
      updateComponent(component.id, {
        properties: {
          ...component.properties,
          locationLocked: true,
          initialView: {
            ...(mapProperties.initialView ?? {}),
            center: [vs.longitude, vs.latitude],
            zoom: vs.zoom,
            pitch: vs.pitch ?? 0,
            bearing: vs.bearing ?? 0,
          },
          _action: undefined,
          _actionTimestamp: undefined,
        },
      });
      return;
    }

    // --- Group actions ---
    const currentGroups: MapGroup[] = mapProperties.groups ?? [];

    // Helper to clear action props after processing
    const clearActionProps = (extra: Record<string, undefined> = {}) => {
      updateComponent(component.id, {
        properties: {
          ...component.properties,
          _action: undefined,
          _actionTimestamp: undefined,
          _groupName: undefined,
          ...extra,
        },
      });
    };

    if (actionName === 'createGroup' || actionName === 'createAreaGroup') {
      const name = actionGroupName ?? actionSensorType ?? 'New Group';
      const groupType: MapGroupType = actionName === 'createAreaGroup'
        ? 'area'
        : actionGroupType ?? 'custom';
      const newGroup: MapGroup = {
        id: `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name,
        type: groupType,
        color: DEFAULT_GROUP_COLORS[currentGroups.length % DEFAULT_GROUP_COLORS.length],
        visible: true,
        sensorIds: [],
        pinIds: [],
        createdAt: new Date().toISOString(),
      };
      updateComponent(component.id, {
        properties: {
          ...component.properties,
          groups: [...currentGroups, newGroup],
          _action: undefined,
          _actionTimestamp: undefined,
          _groupName: undefined,
          _groupType: undefined,
          _sensorType: undefined,
        },
      });
      return;
    }

    if (actionName === 'deleteGroup' && actionGroupName) {
      updateComponent(component.id, {
        properties: {
          ...component.properties,
          groups: currentGroups.filter(g => g.name.toLowerCase() !== actionGroupName.toLowerCase()),
          _action: undefined,
          _actionTimestamp: undefined,
          _groupName: undefined,
        },
      });
      return;
    }

    if (actionName === 'setGroupVisibility' && actionGroupName) {
      const group = currentGroups.find(g => g.name.toLowerCase() === actionGroupName.toLowerCase());
      if (group && actionVisible !== undefined) {
        setLocalGroupOverrides(prev => new Map(prev).set(group.id, actionVisible));
      }
      clearActionProps({ _visible: undefined });
      return;
    }

    if (actionName === 'filterToGroup' && actionGroupName) {
      const targetGroup = currentGroups.find(g => g.name.toLowerCase() === actionGroupName.toLowerCase());
      const newOverrides = new Map<string, boolean>();
      currentGroups.forEach(g => newOverrides.set(g.id, g.id === targetGroup?.id));
      setLocalGroupOverrides(newOverrides);
      clearActionProps();
      return;
    }

    // --- Pin actions ---
    if (actionName === 'deletePin') {
      if (actionPinName) {
        const currentPins = mapProperties.pins ?? [];
        updateComponent(component.id, {
          properties: {
            ...component.properties,
            pins: currentPins.filter(p => p.name?.toLowerCase() !== actionPinName.toLowerCase()),
            _action: undefined,
            _actionTimestamp: undefined,
            _pinName: undefined,
          },
        });
      }
      return;
    }

    if (actionName === 'linkPinToPage') {
      if (actionPinName && actionPageId) {
        const currentPins = mapProperties.pins ?? [];
        updateComponent(component.id, {
          properties: {
            ...component.properties,
            pins: currentPins.map(p =>
              p.name?.toLowerCase() === actionPinName.toLowerCase()
                ? { ...p, navigation: { ...p.navigation, targetPageId: actionPageId } }
                : p
            ),
            _action: undefined,
            _actionTimestamp: undefined,
            _pinName: undefined,
            _pageId: undefined,
          },
        });
      }
      return;
    }

    // Unknown action - clear props to avoid re-triggering
    clearActionProps();
  }, [
    actionName,
    actionTimestamp,
    actionGroupName,
    actionSensorType,
    actionGroupType,
    actionVisible,
    actionPinName,
    actionPageId,
    component.id,
    component.properties,
    mapProperties.initialView,
    mapProperties.groups,
    mapProperties.pins,
    updateComponent,
  ]);

  // Phase 4: Pin placement mode state
  const [cursorCoordinates, setCursorCoordinates] = useState<[number, number] | null>(null);
  const pinPlacementMode = mapProperties.pinPlacementMode ?? false;
  const pendingPinData = mapProperties.pendingPin;

  // ---------------------------------------------------------------------------
  // Phase 8: Performance Monitoring
  // ---------------------------------------------------------------------------
  const {
    startMeasure,
    endMeasure,
    recordMetric,
  } = usePerformanceMonitor();

  const { start: startFPSMonitor, stop: stopFPSMonitor } = useFPSMonitor();

  // Start FPS monitoring when map loads in development.
  useEffect(() => {
    if (mapLoaded && process.env.NODE_ENV === 'development') {
      startFPSMonitor();
      return () => {
        stopFPSMonitor();
      };
    }
    return undefined;
  }, [mapLoaded, startFPSMonitor, stopFPSMonitor]);

  // ---------------------------------------------------------------------------
  // Data Source Configuration & Real-Time Hook (Phase 4)
  // ---------------------------------------------------------------------------
  const dataSource = mapProperties.dataSource as ChartDataSource | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  // Debug log removed - runs on every render
  // console.log('[MapComponent] Data source configured:', dataSource?.type || 'static');

  // Real-time data hook with WebSocket integration
  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: _qtRefetch,
    lastUpdated,
    connectionStatus,
    recentlyUpdated,
    sensorStates,
    isRealTimeEnabled,
    toggleRealTime: _toggleRealTime,
    setRefreshInterval: _setRefreshInterval,
  } = useMapSensorData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate,
    enableRealTime: mapProperties.enableRealTime ?? false,
    refreshInterval: mapProperties.refreshInterval ?? 0,
    groupId: mapProperties.groupId,
    staleThreshold: mapProperties.staleThreshold ?? DEFAULT_REALTIME_CONFIG.staleThreshold,
    offlineThreshold: mapProperties.offlineThreshold ?? DEFAULT_REALTIME_CONFIG.offlineThreshold,
    pins: mapProperties.pins,
  });

  // Combined loading/error state
  const loading = qtLoading;
  const error = qtError;

  // ---------------------------------------------------------------------------
  // Data Priority: Query template > Static data > Default data
  // ---------------------------------------------------------------------------
  const rawSensorData = useMemo(() => {
    if (queryTemplateData && Array.isArray(queryTemplateData)) {
      return queryTemplateData.filter(isSensorDataPoint);
    }
    if (mapProperties.data && Array.isArray(mapProperties.data)) {
      return mapProperties.data;
    }
    return DEFAULT_SENSOR_DATA;
  }, [queryTemplateData, mapProperties.data]);

  // Apply moisture range filter if specified
  const moistureAndZoneFilteredData = useMemo(() => {
    let filtered = rawSensorData;

    // Filter by moisture range
    if (mapProperties.moistureRange) {
      const [min, max] = mapProperties.moistureRange;
      filtered = filtered.filter(
        (s) => s.moistureLevel >= min && s.moistureLevel <= max
      );
    }

    // Filter by selected zones
    const selectedZones = mapProperties.selectedZones ?? [];
    if (selectedZones.length > 0) {
      filtered = filtered.filter(
        (s) => s.zone && selectedZones.includes(s.zone)
      );
    }

    return filtered;
  }, [rawSensorData, mapProperties.moistureRange, mapProperties.selectedZones]);

  // Phase 2: Apply group filtering after moisture/zone filtering
  // Note: We need to use the groups from properties, but effectiveVisibleGroups is computed later
  // So we use a separate memo that depends on the computed effectiveVisibleGroups
  const sensorData = useMemo(() => {
    // Get groups and visible groups from properties (before effectiveVisibleGroups is computed)
    const groupsList = mapProperties.groups ?? [];

    // Build visible groups set from props and local overrides
    const baseVisible = mapProperties.visibleGroups
      ? new Set(mapProperties.visibleGroups)
      : new Set(groupsList.filter((g) => g.visible).map((g) => g.id));

    const visibleGroupsSet = new Set(baseVisible);
    localGroupOverrides.forEach((visible, groupId) => {
      if (visible) {
        visibleGroupsSet.add(groupId);
      } else {
        visibleGroupsSet.delete(groupId);
      }
    });

    // Apply group filtering if groups are defined
    const filtered = filterSensorsByGroups(
      moistureAndZoneFilteredData,
      groupsList,
      visibleGroupsSet
    );

    return filtered;
  }, [moistureAndZoneFilteredData, mapProperties.groups, mapProperties.visibleGroups, localGroupOverrides]);

  // Initialize center from sensor data if not already set in properties
  // This ensures zoom commands have a center to preserve
  useEffect(() => {
    // Only run once when we have sensor data and no center is set
    if (hasInitializedCenter.current) return;
    if (!sensorData || sensorData.length === 0) return;
    if (mapProperties.initialView?.center) return; // Already has center

    // Calculate center from sensor data
    const lngs = sensorData.map(s => s.longitude);
    const lats = sensorData.map(s => s.latitude);
    const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
    const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;

    // Update viewState to center on data
    setViewState(prev => ({
      ...prev,
      longitude: centerLng,
      latitude: centerLat,
    }));

    // Sync to properties so zoom commands preserve this center
    updateComponent(component.id, {
      properties: {
        ...component.properties,
        initialView: {
          ...mapProperties.initialView,
          center: [centerLng, centerLat] as [number, number],
        },
      },
    });

    hasInitializedCenter.current = true;
  }, [sensorData, mapProperties.initialView, component.id, component.properties, updateComponent]);

  // ---------------------------------------------------------------------------
  // Zone Data
  // ---------------------------------------------------------------------------
  const rawZones = actionProperties.zones;
  const zoneData = useMemo(() => {
    const zones = rawZones && rawZones.length > 0 ? rawZones : DEFAULT_ZONE_DATA;
    // Calculate average moisture for each zone based on sensors
    return calculateZoneMoisture(zones, sensorData);
  }, [rawZones, sensorData]);

  // ---------------------------------------------------------------------------
  // Phase 2: Group Data Processing
  // ---------------------------------------------------------------------------
  const groups: MapGroup[] = useMemo(() => {
    return mapProperties.groups ?? [];
  }, [mapProperties.groups]);

  // Compute effective visible groups (combining props with local overrides)
  const effectiveVisibleGroups = useMemo(() => {
    // Start with prop-defined visible groups or all groups if undefined
    const baseVisible = mapProperties.visibleGroups
      ? new Set(mapProperties.visibleGroups)
      : new Set(groups.filter((g) => g.visible).map((g) => g.id));

    // Apply local overrides
    const result = new Set(baseVisible);
    localGroupOverrides.forEach((visible, groupId) => {
      if (visible) {
        result.add(groupId);
      } else {
        result.delete(groupId);
      }
    });

    return result;
  }, [groups, mapProperties.visibleGroups, localGroupOverrides]);

  // Convert groups to GeoJSON for area rendering
  const groupGeoJsonData: MapGroupGeoJSON = useMemo(() => {
    // Only include visible groups in the GeoJSON
    const visibleGroups = groups.filter((g) => effectiveVisibleGroups.has(g.id));
    return convertGroupsToGeoJSON(visibleGroups);
  }, [groups, effectiveVisibleGroups]);

  // ---------------------------------------------------------------------------
  // Phase 3: Pin Data Processing
  // ---------------------------------------------------------------------------
  const pins: MapPinType[] = useMemo(() => {
    return mapProperties.pins ?? [];
  }, [mapProperties.pins]);

  const showPins = mapProperties.showPins !== false;

  // Pin data cache — populated by PinBindingFetcher, read by tooltip display
  const [pinDataCache, setPinDataCache] = useState<Map<string, PinDataCacheEntry>>(new Map());
  const handlePinDataUpdate = useCallback((bindingId: string, entry: PinDataCacheEntry) => {
    setPinDataCache(prev => {
      const next = new Map(prev);
      next.set(bindingId, entry);
      return next;
    });
  }, []);

  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  const selectedPinIdRef = useRef<string | null>(null);
  selectedPinIdRef.current = selectedPinId;
  const componentPropsRef = useRef(component.properties);
  componentPropsRef.current = component.properties;

  // Clear pin selection when map is deselected in editor
  const isComponentSelected = useEditorStore(
    state => state.selectedComponents.includes(component.id)
  );

  useEffect(() => {
    if (!isComponentSelected) {
      if (selectedPinIdRef.current) {
        setSelectedPinId(null);
      }
      if ((componentPropsRef.current as MapComponentProperties).selectedPinId) {
        updateComponent(component.id, {
          properties: { ...componentPropsRef.current, selectedPinId: undefined },
        });
      }
    }
  }, [isComponentSelected, component.id, updateComponent]);

  // Pin drag-to-reposition state
  const [isDraggingPin, setIsDraggingPin] = useState(false);
  const [draggingPinId, setDraggingPinId] = useState<string | null>(null);
  const [draggingPosition, setDraggingPosition] = useState<[number, number] | null>(null);
  const isDraggingPinRef = useRef(false);
  const pinDragJustEndedRef = useRef(false);
  const [isRepositionMode, setIsRepositionMode] = useState(false);

  // Derive the selected pin object
  const selectedPin = useMemo(() => {
    if (!selectedPinId || !pins) return null;
    return pins.find(p => p.id === selectedPinId) ?? null;
  }, [selectedPinId, pins]);

  // Get pages for navigation dropdown in pin properties panel
  const editorPages = useEditorStore(state => state.pages);
  const pagesList = useMemo(() =>
    (editorPages ?? []).map((p: Page) => ({ id: p.id, name: p.name })),
    [editorPages]
  );

  // Convert pins to GeoJSON for rendering
  const hoveredPinId = hoveredPin?.pin.id ?? undefined;

  const pinGeoJSON: MapPinGeoJSON = useMemo(() => {
    if (!pins || pins.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }
    let visiblePins = pins.filter((p) => p.visible !== false);
    // Override dragged pin's coordinates with live cursor position
    if (draggingPinId && draggingPosition) {
      visiblePins = visiblePins.map(p =>
        p.id === draggingPinId
          ? { ...p, longitude: draggingPosition[0], latitude: draggingPosition[1] }
          : p
      );
    }
    return convertPinsToGeoJSON(visiblePins, effectiveVisibleGroups, selectedPinId ?? undefined, hoveredPinId);
  }, [pins, effectiveVisibleGroups, selectedPinId, hoveredPinId, draggingPinId, draggingPosition]);

  // ---------------------------------------------------------------------------
  // Phase 6: Pin Navigation Integration
  // ---------------------------------------------------------------------------
  const pinNavigationConfig = mapProperties.pinNavigation as PinNavigationConfig | undefined;

  // Get interaction context for navigation support
  const interaction = useComponentInteraction(component);

  // Pin navigation hook for handling click-to-navigate
  const {
    handlePinClick: triggerPinNavigation,
    isNavigable: isPinNavigable,
    hasAnyNavigation: hasPinNavigation,
  } = useMapPinNavigation({
    component,
    interaction,
    pins,
    pinNavigation: pinNavigationConfig,
  });

  // Cursor state for navigable pins
  const [pinNavigationCursor, setPinNavigationCursor] = useState<'pointer' | 'default'>('default');

  // ---------------------------------------------------------------------------
  // Phase 8: Optimized Data Processing Pipeline
  // ---------------------------------------------------------------------------

  // Step 1: Viewport Culling - Only process sensors within visible viewport
  const culledSensorData = useMemo(() => {
    if (!mapLoaded || sensorData.length === 0) {
      return sensorData;
    }
    if (!Number.isFinite(viewState.longitude) || !Number.isFinite(viewState.latitude)) {
      return sensorData;
    }

    startMeasure('culling');

    // Get viewport bounds from map
    const mapBounds = mapRef.current?.getBounds();
    const bounds = extractViewportBounds(mapBounds ?? null);

    // Calculate optimal buffer based on zoom
    const buffer = calculateOptimalBuffer(viewState.zoom);

    // Cull sensors to viewport
    const result = cullSensorsByViewport(sensorData, bounds, {
      buffer,
      minSensors: 10,
      maxSensors: 5000,
    });

    const cullingTime = endMeasure('culling');
    recordMetric('cullingTime', cullingTime);
    recordMetric('totalSensors', result.totalCount);
    recordMetric('visibleSensors', result.sensors.length);

    return result.sensors;
  }, [sensorData, mapLoaded, viewState.zoom, viewState.longitude, viewState.latitude, startMeasure, endMeasure, recordMetric]);

  // Step 2: Data Decimation - Reduce density at distant zoom levels
  const decimatedSensorData = useMemo(() => {
    if (culledSensorData.length === 0) {
      return culledSensorData;
    }

    // Get recommended settings based on dataset size
    const settings = getRecommendedDecimationSettings(culledSensorData.length);

    // Apply decimation
    const result = decimateSensors(culledSensorData, viewState.zoom, {
      ...settings,
      // Allow override from properties
      minSensors: mapProperties.minVisibleSensors ?? settings.minSensors,
    });

    return result.sensors;
  }, [culledSensorData, viewState.zoom, mapProperties.minVisibleSensors]);

  // Step 3: Memoized GeoJSON Conversion
  const {
    geoJson: geoJsonData,
    conversionTime,
    fromCache,
    featureCount,
  } = useMemoizedGeoJSON(decimatedSensorData);

  // Record GeoJSON performance metric
  useEffect(() => {
    if (conversionTime > 0) {
      recordMetric('geoJsonTime', conversionTime);
    }
  }, [conversionTime, fromCache, featureCount, recordMetric]);

  // Clear GeoJSON cache on unmount
  useEffect(() => {
    return () => {
      clearGeoJSONCache();
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Zone GeoJSON
  // ---------------------------------------------------------------------------
  const zoneGeoJsonData: ZoneGeoJSON = useMemo(() => {
    return convertZonesToGeoJSON(zoneData);
  }, [zoneData]);

  // ---------------------------------------------------------------------------
  // Pulse Animation GeoJSON (Phase 4)
  // ---------------------------------------------------------------------------
  const pulseGeoJsonData = useMemo(() => {
    if (!sensorData || recentlyUpdated.size === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }
    return convertToPulseGeoJSON(sensorData, recentlyUpdated, sensorStates);
  }, [sensorData, recentlyUpdated, sensorStates]);

  // Offline sensors GeoJSON
  const offlineGeoJsonData = useMemo(() => {
    if (!sensorData) {
      return { type: 'FeatureCollection' as const, features: [] };
    }
    return convertToOfflineGeoJSON(sensorData, sensorStates);
  }, [sensorData, sensorStates]);

  // Check if there are offline sensors
  const hasOfflineSensors = useMemo(() => {
    return Array.from(sensorStates.values()).some(s => s.status === 'offline');
  }, [sensorStates]);

  // ---------------------------------------------------------------------------
  // Supercluster for Clustering
  // ---------------------------------------------------------------------------
  const supercluster = useMemo(() => {
    const config = {
      ...DEFAULT_LAYER_CONFIG.clusters,
      ...mapProperties.layers?.clusters,
    };
    const cluster = createSupercluster(config);
    loadClusterData(cluster, sensorData);
    return cluster;
  }, [sensorData, mapProperties.layers?.clusters]);

  // Get clusters for current viewport
  const clusterData = useMemo(() => {
    if (!mapLoaded || !Number.isFinite(viewState.zoom)) return [];

    const bounds: [number, number, number, number] = [
      viewState.longitude - 180 / Math.pow(2, viewState.zoom),
      viewState.latitude - 90 / Math.pow(2, viewState.zoom),
      viewState.longitude + 180 / Math.pow(2, viewState.zoom),
      viewState.latitude + 90 / Math.pow(2, viewState.zoom),
    ];

    return getClusters(supercluster, bounds, viewState.zoom);
  }, [supercluster, viewState.zoom, viewState.longitude, viewState.latitude, mapLoaded]);

  // Convert clusters to GeoJSON for MapLibre
  const clusterGeoJsonData = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: clusterData,
  }), [clusterData]);

  // ---------------------------------------------------------------------------
  // Layer Configuration
  // ---------------------------------------------------------------------------
  const layerConfig: MapLayerConfig = useMemo(() => ({
    ...DEFAULT_LAYER_CONFIG,
    ...mapProperties.layers,
    heatmap: {
      ...DEFAULT_LAYER_CONFIG.heatmap,
      ...mapProperties.layers?.heatmap,
      ...localLayerOverrides.heatmap,
    },
    markers: {
      ...DEFAULT_LAYER_CONFIG.markers,
      ...mapProperties.layers?.markers,
      ...localLayerOverrides.markers,
    },
    clusters: {
      ...DEFAULT_LAYER_CONFIG.clusters,
      ...mapProperties.layers?.clusters,
      ...localLayerOverrides.clusters,
    },
    zones: {
      ...DEFAULT_LAYER_CONFIG.zones,
      ...mapProperties.layers?.zones,
      ...localLayerOverrides.zones,
    },
  }), [mapProperties.layers, localLayerOverrides]);

  const colorTheme = mapProperties.colorTheme || 'agricultural';
  const heatmapEnabled = layerConfig.heatmap.enabled;
  const markersEnabled = layerConfig.markers.enabled;
  const clusteringEnabled = layerConfig.clusters.enabled || mapProperties.enableClustering;
  const zonesEnabled = layerConfig.zones.enabled;
  const showLabels = layerConfig.markers.showLabels;

  // Debug log removed - runs on every render
  // console.log(`[MapComponent] Layer visibility: heatmap=${heatmapEnabled}, markers=${markersEnabled}, clusters=${clusteringEnabled}, zones=${zonesEnabled}`);

  // ---------------------------------------------------------------------------
  // Map Style URL (supports satellite imagery)
  // ---------------------------------------------------------------------------
  const mapStyleUrl = useMemo(() => {
    const provider = mapProperties.tileProvider || 'osm';
    const style = mapProperties.mapStyle || 'streets';

    switch (provider) {
      case 'maptiler':
        if (maptilerApiKey) {
          // MapTiler supports multiple map styles
          const maptilerStyles: Record<string, string> = {
            streets: 'streets-v2',
            satellite: 'satellite',
            hybrid: 'hybrid',
          };
          const styleName = maptilerStyles[style] || 'streets-v2';
          return `https://api.maptiler.com/maps/${styleName}/style.json?key=${maptilerApiKey}`;
        }
        return 'https://demotiles.maplibre.org/style.json';

      case 'stadia': {
        // Stadia Maps styles (satellite requires Stadia account with imagery access)
        const stadiaStyles: Record<string, string> = {
          streets: 'alidade_smooth',
          satellite: 'alidade_satellite', // Note: May require paid plan
          hybrid: 'alidade_satellite', // Stadia doesn't have separate hybrid
        };
        const stadiaStyle = stadiaStyles[style] || 'alidade_smooth';
        return `https://tiles.stadiamaps.com/styles/${stadiaStyle}.json`;
      }

      case 'osm':
      default:
        // OSM demo tiles only support streets view
        return 'https://demotiles.maplibre.org/style.json';
    }
  }, [mapProperties.tileProvider, mapProperties.mapStyle, maptilerApiKey]);

  // ---------------------------------------------------------------------------
  // Event Handlers (Phase 8: Debounced for Performance)
  // ---------------------------------------------------------------------------

  // Create debounced viewport setter for smooth 60fps during pan/zoom
  const debouncedSetViewState = useMemo(
    () => debounce(
      (newViewState: typeof viewState) => {
        setViewState(newViewState);
      },
      {
        delay: DEBOUNCE_DELAYS.VIEWPORT_UPDATE,
        maxWait: 100, // Force update every 100ms during continuous pan
        leading: true, // Update immediately on first move
        trailing: true, // Update at end of gesture
      }
    ),
    []
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSetViewState.cancel();
    };
  }, [debouncedSetViewState]);

  // Viewport change handler with debouncing
  const handleViewStateChange = useCallback((evt: { viewState: typeof viewState }) => {
    // Skip state updates during programmatic moves (AI commands) to prevent render loops
    // The programmatic move already set the target viewState, we don't need to update during animation
    if (isProgrammaticMove.current) {
      return;
    }
    // Persist latest viewport in module-level map so re-lock captures the
    // user's panned position even if the component remounts in between.
    liveViewports.set(component.id, evt.viewState);
    // Use direct state update for smooth visual feedback
    // The debounced version controls when data updates happen
    debouncedSetViewState(evt.viewState);
  }, [component.id, debouncedSetViewState]);

  // Sync viewState back to component properties on move end (user-initiated moves only)
  // This ensures MapCommandHandler reads the correct current zoom for relative commands
  const handleMoveEnd = useCallback((evt: { viewState: typeof viewState }) => {
    // Skip syncing for programmatic moves (AI commands) to prevent race conditions
    if (isProgrammaticMove.current) {
      return;
    }

    // Skip if updateComponent is not available (e.g., in viewer mode)
    if (!updateComponent) {
      return;
    }

    // Skip if location is locked - auto-sync is disabled
    // Use ref to avoid stale closure issues
    if (isLocationLocked.current) {
      return;
    }

    const newViewState = evt.viewState;
    const currentInitialView = mapProperties.initialView;

    // Only sync if values have changed significantly
    const zoomChanged = Math.abs((currentInitialView?.zoom ?? DEFAULT_MAP_VIEW.zoom) - newViewState.zoom) > 0.01;
    const centerChanged =
      Math.abs((currentInitialView?.center?.[0] ?? DEFAULT_MAP_VIEW.center[0]) - newViewState.longitude) > 0.0001 ||
      Math.abs((currentInitialView?.center?.[1] ?? DEFAULT_MAP_VIEW.center[1]) - newViewState.latitude) > 0.0001;
    const pitchChanged = Math.abs((currentInitialView?.pitch ?? 0) - newViewState.pitch) > 0.1;
    const bearingChanged = Math.abs((currentInitialView?.bearing ?? 0) - newViewState.bearing) > 0.1;

    if (zoomChanged || centerChanged || pitchChanged || bearingChanged) {
      try {
        updateComponent(component.id, {
          properties: {
            ...component.properties,
            initialView: {
              ...currentInitialView,
              zoom: newViewState.zoom,
              center: [newViewState.longitude, newViewState.latitude] as [number, number],
              pitch: newViewState.pitch,
              bearing: newViewState.bearing,
            },
          },
        });
      } catch (error) {
        console.error('[MapComponent] Failed to sync viewState:', error);
      }
    }
  }, [component.id, component.properties, mapProperties.initialView, updateComponent]);

  // Phase 3: Teardrop Marker Loading
  const loadTeardropMarkers = useCallback(async (map: maplibregl.Map, currentPins: MapPinType[]) => {
    try {
      // Collect unique (icon, color) pairs from actual pin data
      const seen = new Map<string, { iconName: PinIconName; color: string }>();
      for (const pin of currentPins) {
        const iconName = mapPinIconToUtilName(pin.style.icon);
        const color = pin.style.color || '#3B82F6';
        const key = `${iconName}-${normalizeColorForImageId(color)}`;
        if (!seen.has(key)) seen.set(key, { iconName, color });
      }
      const specs = Array.from(seen.values());
      if (specs.length === 0) return;

      const markers = await preloadTeardropMarkers(specs);
      for (const { name, image } of markers) {
        if (!map.hasImage(name)) {
          map.addImage(name, image, { sdf: false, pixelRatio: 2 });
        }
      }
    } catch (error) {
      console.error('[MapComponent] Failed to load teardrop markers:', error);
    }
  }, []);

  const handleMapLoad = useCallback(async (evt: maplibregl.MapLibreEvent) => {
    mapRef.current = evt.target;
    setMapLoaded(true);

    // Load teardrop marker images BEFORE pin layers render to avoid "image not found" warnings
    await loadTeardropMarkers(evt.target, pins);
    setMarkersLoaded(true);

    // Re-add marker images on demand if a style change wipes them
    evt.target.on('styleimagemissing', (e: { id: string }) => {
      if (e.id.startsWith('pin-marker-')) {
        loadTeardropMarkers(evt.target, pins);
      }
    });
  }, [loadTeardropMarkers, pins]);

  // Reload teardrop markers when pins change (new colors/icons)
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !pins.length) return;
    loadTeardropMarkers(mapRef.current, pins).then(() => setMarkersLoaded(true));
  }, [pins, mapLoaded, loadTeardropMarkers]);

  const handleMouseEnter = useCallback((e: maplibregl.MapLayerMouseEvent) => {
    // Suppress hover tooltips during pin drag
    if (isDraggingPinRef.current) return;

    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const props = feature.properties;
      const layerId = feature.layer?.id;

      // Check if it's a pin layer
      if (layerId === PIN_LAYER_ID) {
        const pin = pins.find((p) => p.id === props?.id);
        if (pin) {
          setHoveredPin({
            pin,
            longitude: pin.longitude,
            latitude: pin.latitude,
          });
        }
        return;
      }

      // Check if it's a cluster
      if (props?.cluster && isClusterPoint(feature)) {
        setHoveredCluster(feature);
        return;
      }

      // Find the full sensor data
      const sensor = sensorData.find((s) => s.id === props?.id);
      if (sensor) {
        setHoveredSensor({
          sensor,
          x: e.point.x,
          y: e.point.y,
        });
      }
    }
  }, [sensorData, pins]);

  const handleMouseLeave = useCallback(() => {
    setHoveredSensor(null);
    setHoveredCluster(null);
    setHoveredPin(null);
  }, []);

  const handleClick = useCallback((e: maplibregl.MapLayerMouseEvent) => {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const props = feature.properties;

      // Handle cluster click - zoom to expand
      if (props?.cluster && props?.cluster_id) {
        const expansionZoom = getClusterExpansionZoom(supercluster, props.cluster_id);
        if (feature.geometry.type !== 'Point') {
          return;
        }
        const coordinates = feature.geometry.coordinates as [number, number];

        setViewState(prev => ({
          ...prev,
          longitude: coordinates[0],
          latitude: coordinates[1],
          zoom: Math.min(expansionZoom + 1, 18),
        }));
        return;
      }

      // Handle sensor click
      if (onSensorClick) {
        const sensor = sensorData.find((s) => s.id === props?.id);
        if (sensor) {
          onSensorClick(sensor);
        }
      }
    }
  }, [sensorData, onSensorClick, supercluster]);

  // ---------------------------------------------------------------------------
  // Phase 5: Control Handlers
  // ---------------------------------------------------------------------------

  // Track container dimensions for responsive controls
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
        setContainerHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Zoom change handler
  const handleZoomChange = useCallback((newZoom: number) => {
    setViewState(prev => ({ ...prev, zoom: newZoom }));
  }, []);

  // Bearing change handler
  const handleBearingChange = useCallback((newBearing: number) => {
    setViewState(prev => ({ ...prev, bearing: newBearing }));
  }, []);

  // Fit to bounds handler
  const handleFitToBounds = useCallback(() => {
    // Collect all valid coordinate points from sensors and pins
    const points: { lng: number; lat: number }[] = [];

    sensorData.forEach(s => {
      const lng = Number(s.longitude);
      const lat = Number(s.latitude);
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        points.push({ lng, lat });
      }
    });

    pins.forEach(p => {
      const lng = Number(p.longitude);
      const lat = Number(p.latitude);
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        points.push({ lng, lat });
      }
    });

    if (points.length === 0) return;

    // Calculate bounds from all data points
    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    points.forEach(({ lng, lat }) => {
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });

    // Add padding
    const lngPadding = (maxLng - minLng) * 0.1 || 0.01;
    const latPadding = (maxLat - minLat) * 0.1 || 0.01;

    // Calculate center and zoom
    const centerLng = (minLng + maxLng) / 2;
    const centerLat = (minLat + maxLat) / 2;

    // Estimate zoom level based on bounds
    const lngSpan = maxLng - minLng + lngPadding * 2;
    const latSpan = maxLat - minLat + latPadding * 2;
    let zoom = Math.min(
      18,
      Math.max(
        1,
        Math.floor(Math.log2(360 / Math.max(lngSpan, latSpan)))
      )
    );

    // Final NaN guard - fall back to current zoom
    if (!Number.isFinite(zoom)) {
      zoom = viewState.zoom;
    }

    setViewState(prev => ({
      ...prev,
      longitude: centerLng,
      latitude: centerLat,
      zoom,
    }));
  }, [sensorData, pins, viewState.zoom]);

  // Fullscreen toggle handler
  const handleFullscreenToggle = useCallback(() => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Phase 2: Group visibility handlers
  const handleGroupVisibilityChange = useCallback((groupId: string, visible: boolean) => {
    setLocalGroupOverrides(prev => {
      const next = new Map(prev);
      next.set(groupId, visible);
      return next;
    });
  }, []);

  const handleToggleAllGroups = useCallback((visible: boolean) => {
    const groupsList = mapProperties.groups ?? [];
    setLocalGroupOverrides(() => {
      const next = new Map<string, boolean>();
      groupsList.forEach((group) => {
        next.set(group.id, visible);
      });
      return next;
    });
  }, [mapProperties.groups]);

  // Render mode handler for LayerPanel
  const handleRenderModeChange = useCallback((mode: 'heatmap' | 'markers' | 'clusters') => {
    setLocalLayerOverrides({
      heatmap: { enabled: mode === 'heatmap' },
      markers: { enabled: mode === 'markers' },
      clusters: { enabled: mode === 'clusters' },
    });
  }, []);

  const handleZonesToggle = useCallback((enabled: boolean) => {
    setLocalLayerOverrides(prev => ({ ...prev, zones: { enabled } }));
  }, []);

  const activeRenderMode = useMemo((): 'heatmap' | 'markers' | 'clusters' => {
    if (clusteringEnabled) return 'clusters';
    if (heatmapEnabled) return 'heatmap';
    return 'markers';
  }, [heatmapEnabled, clusteringEnabled]);

  // ---------------------------------------------------------------------------
  // Phase 4: Pin Placement Mode Handlers
  // ---------------------------------------------------------------------------

  // Pin placement hook integration
  const handlePinCreated = useCallback((newPin: MapPinType) => {
    // Add the new pin to the pins array
    const currentPins = mapProperties.pins ?? [];
    const updatedPins = [...currentPins, newPin];

    // Update component properties with new pin and exit placement mode
    updateComponent(component.id, {
      properties: {
        ...component.properties,
        pins: updatedPins,
        pinPlacementMode: false,
        pendingPin: undefined,
      },
    });
  }, [component.id, component.properties, mapProperties.pins, updateComponent]);

  const handlePinPlacementCancel = useCallback(() => {
    updateComponent(component.id, {
      properties: {
        ...component.properties,
        pinPlacementMode: false,
        pendingPin: undefined,
      },
    });
  }, [component.id, component.properties, updateComponent]);

  // ---------------------------------------------------------------------------
  // Pin Selection Handlers
  // ---------------------------------------------------------------------------

  // Select or deselect a pin (local UI state only — no store round-trip)
  const handlePinSelect = useCallback((pinId: string | null) => {
    setSelectedPinId(pinId);
    // Persist to properties so AIPropertiesChat can detect pin selection
    updateComponent(component.id, {
      properties: { ...componentPropsRef.current, selectedPinId: pinId ?? undefined },
    });
  }, [component.id, updateComponent]);

  // Update a pin's properties
  const handleUpdatePin = useCallback((pinId: string, updates: Partial<MapPinType>) => {
    const currentPins = (componentPropsRef.current as MapComponentProperties).pins ?? [];
    const updatedPins = currentPins.map(p =>
      p.id === pinId
        ? { ...p, ...updates, updatedAt: new Date().toISOString() }
        : p
    );
    updateComponent(component.id, {
      properties: {
        ...componentPropsRef.current,
        pins: updatedPins,
      },
    });
  }, [component.id, updateComponent]);

  // Delete a pin
  const handleDeletePin = useCallback((pinId: string) => {
    const currentPins = (componentPropsRef.current as MapComponentProperties).pins ?? [];
    setSelectedPinId(null);
    updateComponent(component.id, {
      properties: {
        ...componentPropsRef.current,
        pins: currentPins.filter(p => p.id !== pinId),
        selectedPinId: undefined,
      },
    });
  }, [component.id, updateComponent]);

  // Request data binding for a pin (triggers AI chat discovery)
  const handleRequestPinDataBinding = useCallback((pinId: string) => {
    updateComponent(component.id, {
      properties: { ...componentPropsRef.current, pinDataBindingRequest: pinId },
    });
  }, [component.id, updateComponent]);

  // Close the properties panel (deselect)
  const handlePinPropertiesClose = useCallback(() => {
    setIsRepositionMode(false);
    handlePinSelect(null);
  }, [handlePinSelect]);

  // Escape key to cancel pin drag or deselect pin
  useEffect(() => {
    if (!selectedPinId && !isDraggingPin) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Cancel drag without persisting — pin snaps back to stored position
        if (isDraggingPinRef.current) {
          setIsDraggingPin(false);
          isDraggingPinRef.current = false;
          setDraggingPinId(null);
          setDraggingPosition(null);
          setIsRepositionMode(false);
          mapRef.current?.dragPan.enable();
          return;
        }
        // Exit reposition mode if active (without deselecting)
        if (isRepositionMode) {
          setIsRepositionMode(false);
          return;
        }
        handlePinSelect(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPinId, isDraggingPin, isRepositionMode, handlePinSelect]);

  // Clear selection when entering pin placement mode
  useEffect(() => {
    if (pinPlacementMode && selectedPinId) {
      handlePinSelect(null);
    }
  }, [pinPlacementMode, selectedPinId, handlePinSelect]);

  const {
    isPlacementMode,
    pendingPin: activePendingPin,
    cursorPosition: hookCursorPosition,
    confirmPlacement,
    exitPlacementMode,
    setCursorPosition: setHookCursorPosition,
    updatePendingPin,
  } = useMapPinPlacement({
    enabled: pinPlacementMode,
    pendingPin: pendingPinData,
    existingPins: pins,
    onPinCreated: handlePinCreated,
    onCancel: handlePinPlacementCancel,
  });

  // Use hook cursor position, fallback to local state
  const effectiveCursorPosition = hookCursorPosition ?? cursorCoordinates;

  // ---------------------------------------------------------------------------
  // Pin drag-to-reposition handlers
  // ---------------------------------------------------------------------------

  const handlePinDragStart = useCallback((e: maplibregl.MapLayerMouseEvent) => {
    // Don't start drag in functional/viewer mode or placement mode
    if (interaction.mode === 'functional' || isPlacementMode) return;
    // Only allow drag when reposition mode is explicitly active
    if (!isRepositionMode) return;

    // Find pin feature from e.features (populated by interactiveLayerIds)
    let pinFeature = e.features?.find(f => f.layer?.id === PIN_LAYER_ID);

    // Fallback: query rendered features at click point
    if (!pinFeature && showPins && pins.length > 0) {
      const map = e.target;
      if (map?.getLayer?.(PIN_LAYER_ID)) {
        const directFeatures = map.queryRenderedFeatures(e.point, { layers: [PIN_LAYER_ID] });
        if (directFeatures.length > 0) {
          pinFeature = directFeatures[0];
        }
      }
    }

    if (!pinFeature) return;

    const pinId = pinFeature.properties?.id as string | undefined;
    if (!pinId) return;

    const pin = pins.find(p => p.id === pinId);
    if (!pin) return;

    setDraggingPinId(pinId);
    setDraggingPosition([pin.longitude, pin.latitude]);
    setIsDraggingPin(true);
    isDraggingPinRef.current = true;

    // Select the pin if not already selected
    if (selectedPinIdRef.current !== pinId) {
      handlePinSelect(pinId);
    }

    // Disable map panning during drag
    mapRef.current?.dragPan.disable();
  }, [interaction.mode, isPlacementMode, isRepositionMode, showPins, pins, handlePinSelect]);

  const handlePinDragEnd = useCallback((e: maplibregl.MapLayerMouseEvent) => {
    if (!isDraggingPinRef.current || !draggingPinId) return;

    const newLng = e.lngLat.lng;
    const newLat = e.lngLat.lat;

    // Find original pin to check movement threshold (~1m)
    const originalPin = pins.find(p => p.id === draggingPinId);
    const movedEnough = originalPin
      ? Math.abs(newLng - originalPin.longitude) > 0.00001 || Math.abs(newLat - originalPin.latitude) > 0.00001
      : false;

    if (movedEnough) {
      handleUpdatePin(draggingPinId, { longitude: newLng, latitude: newLat });
    }

    // Clear drag state
    setIsDraggingPin(false);
    isDraggingPinRef.current = false;
    setDraggingPinId(null);
    setDraggingPosition(null);
    mapRef.current?.dragPan.enable();

    // Prevent click event from firing after drag
    pinDragJustEndedRef.current = true;
    requestAnimationFrame(() => { pinDragJustEndedRef.current = false; });
  }, [draggingPinId, pins, handleUpdatePin]);

  // Window mouseup fallback — completes drag even if cursor leaves map container
  useEffect(() => {
    if (!isDraggingPin) return;

    const handleWindowMouseUp = () => {
      if (!isDraggingPinRef.current || !draggingPinId) return;

      // Commit last known dragging position
      if (draggingPosition) {
        const originalPin = pins.find(p => p.id === draggingPinId);
        const movedEnough = originalPin
          ? Math.abs(draggingPosition[0] - originalPin.longitude) > 0.00001 || Math.abs(draggingPosition[1] - originalPin.latitude) > 0.00001
          : false;

        if (movedEnough) {
          handleUpdatePin(draggingPinId, { longitude: draggingPosition[0], latitude: draggingPosition[1] });
        }
      }

      setIsDraggingPin(false);
      isDraggingPinRef.current = false;
      setDraggingPinId(null);
      setDraggingPosition(null);
      mapRef.current?.dragPan.enable();

      pinDragJustEndedRef.current = true;
      requestAnimationFrame(() => { pinDragJustEndedRef.current = false; });
    };

    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => window.removeEventListener('mouseup', handleWindowMouseUp);
  }, [isDraggingPin, draggingPinId, draggingPosition, pins, handleUpdatePin]);

  // Guard against double-fire of map click events
  const lastClickRef = useRef(0);

  // Combined map click handler - handles pin placement first, then pin navigation, then delegates to original
  const handleMapClick = useCallback((e: maplibregl.MapLayerMouseEvent) => {
    // Suppress click that fires immediately after a drag release
    if (pinDragJustEndedRef.current) {
      pinDragJustEndedRef.current = false;
      return;
    }

    // Debounce rapid duplicate fires
    const now = Date.now();
    if (now - lastClickRef.current < 100) return;
    lastClickRef.current = now;

    // Skip clicks on UI overlays (PinPropertiesPanel, controls, etc.)
    // Native DOM events from overlays reach maplibre before React's stopPropagation
    const target = e.originalEvent?.target;
    if (target instanceof HTMLElement && !target.closest('.maplibregl-canvas')) {
      return;
    }

    // Handle pin placement mode first
    if (isPlacementMode) {
      const coordinates: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      confirmPlacement(coordinates);
      return;
    }

    // Pin selection in non-functional mode (editor uses 'viewing' mode)
    if (interaction.mode !== 'functional') {
      // Always query the map directly for pin features — more reliable than e.features
      // which can be stale or unpopulated after re-renders from prior pin selection
      let pinFeature: maplibregl.MapGeoJSONFeature | undefined;
      const map = e.target;

      if (showPins && pins.length > 0 && map?.getLayer?.(PIN_LAYER_ID)) {
        // Use a small bbox around the click point for reliable icon hit detection
        const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
          [e.point.x - 5, e.point.y - 5],
          [e.point.x + 5, e.point.y + 5],
        ];
        const directFeatures = map.queryRenderedFeatures(bbox, { layers: [PIN_LAYER_ID] });

        if (directFeatures.length > 0) {
          // When a pin is already selected and multiple pins overlap at the click point,
          // prefer a different pin so the user can switch between nearby pins
          const currentSelectedId = selectedPinIdRef.current;
          if (currentSelectedId && directFeatures.length > 1) {
            pinFeature = directFeatures.find(f => f.properties?.id !== currentSelectedId) ?? directFeatures[0];
          } else {
            pinFeature = directFeatures[0];
          }
        }
      }

      // Fallback: check e.features (populated by react-map-gl via interactiveLayerIds)
      if (!pinFeature) {
        pinFeature = e.features?.find(f => f.layer?.id === PIN_LAYER_ID) as maplibregl.MapGeoJSONFeature | undefined;
      }

      if (pinFeature) {
        const pinId = pinFeature.properties?.id;
        if (pinId) {
          handlePinSelect(pinId as string);
          return;
        }
      }

      // Clicking on empty map area — deselect pin if one is selected
      if (selectedPinIdRef.current) {
        handlePinSelect(null);
        return;
      }
    }

    // Phase 6: Handle pin navigation in functional mode
    if (interaction.mode === 'functional' && hasPinNavigation && e.features && e.features.length > 0) {
      const feature = e.features[0];
      const layerId = feature.layer?.id;

      // Check if clicking on a pin
      if (layerId === PIN_LAYER_ID) {
        const pinId = feature.properties?.id;
        if (pinId && isPinNavigable(pinId)) {
          triggerPinNavigation(pinId);
          return; // Don't process other click handlers
        }
      }
    }

    // Delegate to original click handler for clusters, sensors, etc.
    handleClick(e);
  }, [isPlacementMode, confirmPlacement, handleClick, interaction.mode, hasPinNavigation, isPinNavigable, triggerPinNavigation, handlePinSelect, showPins, pins.length]);

  // Map mouse move handler for pin placement coordinates and navigation cursor
  const handleMapMouseMove = useCallback((e: maplibregl.MapLayerMouseEvent) => {
    // Handle pin drag-to-reposition
    if (isDraggingPinRef.current && draggingPinId) {
      setDraggingPosition([e.lngLat.lng, e.lngLat.lat]);
      return;
    }

    // Handle pin placement mode first
    if (isPlacementMode) {
      const coords: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      setCursorCoordinates(coords);
      setHookCursorPosition(coords);
      return;
    }

    // Phase 6: Check for navigable pins and update cursor
    // Only check in functional mode when pin navigation is available
    if (interaction.mode === 'functional' && hasPinNavigation && e.features && e.features.length > 0) {
      const feature = e.features[0];
      const layerId = feature.layer?.id;

      // Check if hovering over a pin
      if (layerId === PIN_LAYER_ID) {
        const pinId = feature.properties?.id;
        if (pinId && isPinNavigable(pinId)) {
          setPinNavigationCursor('pointer');
          return;
        }
      }
    }

    // Reset cursor to default if not over a navigable pin
    if (pinNavigationCursor !== 'default') {
      setPinNavigationCursor('default');
    }
  }, [isPlacementMode, setHookCursorPosition, interaction.mode, hasPinNavigation, isPinNavigable, pinNavigationCursor, draggingPinId]);

  // ---------------------------------------------------------------------------
  // Phase 5: Controls & Legend Configuration
  // ---------------------------------------------------------------------------

  const controlsConfig: MapControlsConfig = useMemo(() => ({
    ...DEFAULT_CONTROLS_CONFIG,
    ...mapProperties.controlsConfig,
  }), [mapProperties.controlsConfig]);

  const legendConfig: MapLegendConfig = useMemo(() => ({
    ...DEFAULT_LEGEND_CONFIG,
    ...mapProperties.legendConfig,
  }), [mapProperties.legendConfig]);

  // ---------------------------------------------------------------------------
  // Display Settings
  // ---------------------------------------------------------------------------
  const showControls = mapProperties.showControls !== false;
  const showLegend = mapProperties.showLegend !== false;
  const showTooltip = mapProperties.showTooltip !== false;

  // ---------------------------------------------------------------------------
  // Container Styling
  // ---------------------------------------------------------------------------
  // Extract visual styles, excluding dimension properties that are controlled by the wrapper
  const { width: _w, minWidth: _mw, maxWidth: _xw, height: _h, minHeight: _mh, ...visualStyle } = style as React.CSSProperties;

  // CRITICAL: MapComponent uses height: 100% to fill its FlexContainer wrapper.
  // The wrapper's height is controlled by responsivePosition.breakpoints[breakpoint].style.height.
  // Do NOT use component.size.height here - that's legacy and not updated by AI commands.
  // The wrapper handles sizing; this component just fills it.
  const containerStyle: React.CSSProperties = {
    ...visualStyle,
    position: 'relative',
    height: '100%',
    width: '100%',
    minHeight: typeof mapProperties.minHeight === 'string' || typeof mapProperties.minHeight === 'number'
      ? mapProperties.minHeight
      : undefined,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Clip content to container bounds
  };

  // Interactive layer IDs for hover/click
  const interactiveLayerIds = useMemo(() => {
    const ids: string[] = [];
    if (markersEnabled && !clusteringEnabled) ids.push(MARKER_LAYER_ID);
    if (clusteringEnabled) {
      ids.push(CLUSTER_LAYER_ID, UNCLUSTERED_POINT_LAYER_ID);
    }
    if (zonesEnabled) ids.push(ZONE_FILL_LAYER_ID);
    // Phase 3: Add pin layer for hover/click interactions
    if (showPins && pins.length > 0) ids.push(PIN_LAYER_ID);
    return ids;
  }, [markersEnabled, clusteringEnabled, zonesEnabled, showPins, pins.length]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="w-full h-full" style={containerStyle} ref={containerRef}>
      {/* Eagerly fetch pin data so the refresh button can trigger re-fetches */}
      {pins.filter(p => (p.dataBindings?.length ?? 0) > 0).flatMap(pin =>
        (pin.dataBindings ?? []).map(binding => (
          <PinBindingFetcher
            key={binding.id}
            binding={binding}
            componentId={component.id}
            onData={handlePinDataUpdate}
          />
        ))
      )}
      {/* Map Container */}
      <div ref={mapContainerRef} style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
        {(loading || !apiKeyResolved) ? (
          <ChartLoadingSkeleton variant="map" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : (
          <MapGL
            {...viewState}
            onMove={handleViewStateChange}
            onMoveEnd={handleMoveEnd}
            onLoad={handleMapLoad}
            mapLib={maplibregl}
            mapStyle={mapStyleUrl}
            style={{
              width: '100%',
              height: '100%',
              // Priority: dragging > reposition mode > pin placement mode > pin navigation cursor > editing pin hover > default
              cursor: isDraggingPin
                ? 'grabbing'
                : isRepositionMode
                  ? 'grab'
                  : isPlacementMode
                    ? 'none'
                    : pinNavigationCursor !== 'default'
                      ? pinNavigationCursor
                      : (interaction.mode !== 'functional' && hoveredPin)
                        ? 'pointer'
                        : undefined,
            }}
            minZoom={mapProperties.minZoom ?? 1}
            maxZoom={mapProperties.maxZoom ?? 18}
            interactiveLayerIds={interactiveLayerIds.length > 0 ? interactiveLayerIds : undefined}
            onMouseEnter={(showTooltip || interaction.mode !== 'functional') ? handleMouseEnter : undefined}
            onMouseLeave={(showTooltip || interaction.mode !== 'functional') ? handleMouseLeave : undefined}
            // Enable mouse move for pin placement mode, pin drag, pin navigation cursor tracking, or editor mode hover
            onMouseMove={(isDraggingPin || isPlacementMode || interaction.mode !== 'functional' || hasPinNavigation) ? handleMapMouseMove : undefined}
            onMouseDown={handlePinDragStart}
            onMouseUp={handlePinDragEnd}
            onClick={handleMapClick}
            // Phase 7: Touch gesture support (enabled by default in react-map-gl)
            touchZoomRotate={true}
            touchPitch={true}
            dragRotate={true}
            dragPan={!isDraggingPin}
            scrollZoom={true}
            doubleClickZoom={true}
            // Accessibility
            keyboard={true}
            aria-label={isPlacementMode
              ? "Interactive map - click to place pin"
              : "Interactive map with sensor data visualization"}
          >
            {/* Phase 5: Custom Navigation Controls */}
            {showControls && (
              <MapNavigationControls
                zoom={viewState.zoom}
                bearing={viewState.bearing}
                minZoom={mapProperties.minZoom ?? 1}
                maxZoom={mapProperties.maxZoom ?? 18}
                onZoomChange={handleZoomChange}
                onBearingChange={handleBearingChange}
                onFitToBounds={handleFitToBounds}
                sensorData={sensorData}
                pins={pins}
                config={controlsConfig}
                containerWidth={containerWidth}
                containerHeight={containerHeight}
                isFullscreen={isFullscreen}
                onFullscreenToggle={handleFullscreenToggle}
              />
            )}

            {/* Phase 5: Scale Indicator (Phase 7: Responsive) */}
            {showControls && controlsConfig.showScale && (
              <MapScaleIndicator
                zoom={viewState.zoom}
                latitude={viewState.latitude}
                position="bottom-left"
                containerWidth={containerWidth}
              />
            )}

            {/* Zone Overlay Source (rendered below sensors) */}
            {zonesEnabled && (
              <Source id={ZONE_SOURCE_ID} type="geojson" data={zoneGeoJsonData}>
                {/* Zone Fill Layer */}
                <Layer
                  id={ZONE_FILL_LAYER_ID}
                  type="fill"
                  paint={getZoneFillPaint(layerConfig.zones)}
                  beforeId={heatmapEnabled ? HEATMAP_LAYER_ID : undefined}
                />
                {/* Zone Stroke Layer */}
                <Layer
                  id={ZONE_STROKE_LAYER_ID}
                  type="line"
                  paint={getZoneStrokePaint(layerConfig.zones)}
                />
                {/* Zone Labels */}
                <Layer
                  id={ZONE_LABEL_LAYER_ID}
                  type="symbol"
                  paint={getZoneLabelPaint()}
                  layout={getZoneLabelLayout()}
                />
              </Source>
            )}

            {/* Phase 2: Group Area Overlay Source (rendered below sensors, above zones) */}
            {groups.length > 0 && groupGeoJsonData.features.length > 0 && (
              <Source id={GROUP_AREA_SOURCE_ID} type="geojson" data={groupGeoJsonData}>
                {/* Group Area Fill Layer */}
                <Layer
                  id={GROUP_AREA_FILL_LAYER_ID}
                  type="fill"
                  paint={getGroupFillPaint({ fillOpacity: 0.2 })}
                  beforeId={heatmapEnabled ? HEATMAP_LAYER_ID : undefined}
                />
                {/* Group Area Stroke Layer */}
                <Layer
                  id={GROUP_AREA_STROKE_LAYER_ID}
                  type="line"
                  paint={getGroupStrokePaint({ strokeWidth: 2 })}
                />
              </Source>
            )}

            {/* Sensor Data Source (Heatmap + Markers) */}
            {!clusteringEnabled && (
              <Source id="sensors" type="geojson" data={geoJsonData}>
                {/* Heatmap Layer */}
                {heatmapEnabled && (
                  <Layer
                    id={HEATMAP_LAYER_ID}
                    type="heatmap"
                    paint={getHeatmapPaint(colorTheme, layerConfig.heatmap)}
                  />
                )}

                {/* Marker Layer - visible at high zoom or when heatmap is disabled */}
                {markersEnabled && (
                  <Layer
                    id={MARKER_LAYER_ID}
                    type="circle"
                    paint={getMarkerPaint(layerConfig.markers, heatmapEnabled)}
                    minzoom={heatmapEnabled ? 10 : 0}
                  />
                )}

                {/* Marker Labels */}
                {markersEnabled && showLabels && (
                  <Layer
                    id={MARKER_LABEL_LAYER_ID}
                    type="symbol"
                    paint={getMarkerLabelPaint()}
                    layout={getMarkerLabelLayout()}
                    minzoom={13}
                  />
                )}
              </Source>
            )}

            {/* Cluster Data Source */}
            {clusteringEnabled && (
              <Source
                id="clusters"
                type="geojson"
                data={clusterGeoJsonData}
              >
                {/* Cluster Circles */}
                <Layer
                  id={CLUSTER_LAYER_ID}
                  type="circle"
                  filter={['has', 'point_count']}
                  paint={getClusterPaint()}
                />

                {/* Cluster Count Labels */}
                <Layer
                  id={CLUSTER_COUNT_LAYER_ID}
                  type="symbol"
                  filter={['has', 'point_count']}
                  paint={getClusterCountPaint()}
                  layout={getClusterCountLayout()}
                />

                {/* Unclustered Points */}
                <Layer
                  id={UNCLUSTERED_POINT_LAYER_ID}
                  type="circle"
                  filter={['!', ['has', 'point_count']]}
                  paint={getUnclusteredPointPaint()}
                />
              </Source>
            )}

            {/* Phase 3: Custom Pin Layer — gate on markersLoaded to avoid "image not found" race */}
            {showPins && markersLoaded && pinGeoJSON.features.length > 0 && (
              <Source id={PIN_SOURCE_ID} type="geojson" data={pinGeoJSON}>
                {/* Pin Icons (teardrop markers with baked-in color) */}
                <Layer
                  id={PIN_LAYER_ID}
                  type="symbol"
                  layout={getPinSymbolLayout()}
                  paint={getPinSymbolPaint()}
                />
                {/* Pin Labels (visible at higher zoom) */}
                <Layer
                  id={PIN_LABELS_LAYER_ID}
                  type="symbol"
                  layout={getPinLabelLayout()}
                  paint={getPinLabelPaint()}
                  minzoom={12}
                />
                {/* Phase 6: Navigation Indicator Badge */}
                {hasPinNavigation && (
                  <Layer
                    id={PIN_NAV_INDICATOR_LAYER_ID}
                    type="symbol"
                    layout={getPinNavIndicatorLayout()}
                    paint={getPinNavIndicatorPaint()}
                  />
                )}
              </Source>
            )}

            {/* Pulse Animation Layer - Recently Updated Sensors (Phase 4) */}
            {isRealTimeEnabled && recentlyUpdated.size > 0 && (
              <Source
                id={UPDATE_PULSE_SOURCE_ID}
                type="geojson"
                data={pulseGeoJsonData}
              >
                <Layer
                  id={UPDATE_PULSE_LAYER_ID}
                  type="circle"
                  paint={getUpdatePulseLayerPaint() as CircleLayerSpecification['paint']}
                />
              </Source>
            )}

            {/* Offline Sensors Indicator Layer (Phase 4) */}
            {hasOfflineSensors && (
              <Source
                id="offline-sensors"
                type="geojson"
                data={offlineGeoJsonData}
              >
                <Layer
                  id={OFFLINE_INDICATOR_LAYER_ID}
                  type="circle"
                  paint={getOfflineIndicatorPaint()}
                />
              </Source>
            )}

            {/* Sensor Tooltip Popup */}
            {showTooltip && hoveredSensor && (
              <Popup
                longitude={hoveredSensor.sensor.longitude}
                latitude={hoveredSensor.sensor.latitude}
                anchor="bottom"
                closeButton={false}
                closeOnClick={false}
                offset={15}
              >
                <div className="p-2 min-w-[140px]">
                  <div className="flex items-center gap-1 mb-1">
                    <MapPin className="w-3 h-3 text-muted-foreground" />
                    <span className="font-medium text-sm">
                      {hoveredSensor.sensor.deviceId}
                    </span>
                  </div>
                  <div className="text-xs space-y-0.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Moisture:</span>
                      <span
                        className="font-medium"
                        style={{ color: getMoistureStatus(hoveredSensor.sensor.moistureLevel).color }}
                      >
                        {hoveredSensor.sensor.moistureLevel}%
                        <span className="text-[10px] ml-1">
                          ({getMoistureStatus(hoveredSensor.sensor.moistureLevel).label})
                        </span>
                      </span>
                    </div>
                    {hoveredSensor.sensor.temperature !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Temp:</span>
                        <span>{hoveredSensor.sensor.temperature}°</span>
                      </div>
                    )}
                    {hoveredSensor.sensor.zone && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Zone:</span>
                        <span>{hoveredSensor.sensor.zone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Popup>
            )}

            {/* Cluster Tooltip Popup */}
            {showTooltip && hoveredCluster && hoveredCluster.properties?.cluster && (
              <Popup
                longitude={hoveredCluster.geometry.coordinates[0]}
                latitude={hoveredCluster.geometry.coordinates[1]}
                anchor="bottom"
                closeButton={false}
                closeOnClick={false}
                offset={20}
              >
                <div className="p-2 min-w-[100px]">
                  <div className="flex items-center gap-1 mb-1">
                    <Layers className="w-3 h-3 text-muted-foreground" />
                    <span className="font-medium text-sm">Cluster</span>
                  </div>
                  <div className="text-xs space-y-0.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sensors:</span>
                      <span className="font-medium">
                        {hoveredCluster.properties?.point_count || 0}
                      </span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      Click to expand
                    </div>
                  </div>
                </div>
              </Popup>
            )}

            {/* Phase 3: Pin Tooltip Popup */}
            {showTooltip && hoveredPin && (
              <Popup
                longitude={hoveredPin.longitude}
                latitude={hoveredPin.latitude}
                anchor="bottom"
                closeButton={false}
                closeOnClick={false}
                offset={[0, -70] as [number, number]}
              >
                <div className="p-2 min-w-[140px] max-w-[220px]">
                  <div className="flex items-center gap-1 mb-1">
                    <MapPin className="w-3 h-3" style={{ color: hoveredPin.pin.style.color }} />
                    <span className="font-medium text-sm">{hoveredPin.pin.name}</span>
                  </div>
                  {hoveredPin.pin.description && (
                    <p className="text-xs text-muted-foreground mb-1">
                      {hoveredPin.pin.description}
                    </p>
                  )}
                  {(hoveredPin.pin.dataBindings?.length ?? 0) > 0 && (
                    <div className="space-y-1 py-1 border-t border-b border-gray-100 my-1">
                      {hoveredPin.pin.dataBindings?.map(b => (
                        <PinTooltipDataDisplay key={b.id} binding={b} cached={pinDataCache.get(b.id)} />
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {hoveredPin.pin.latitude.toFixed(4)}, {hoveredPin.pin.longitude.toFixed(4)}
                  </div>
                  {hoveredPin.pin.navigation?.targetPageId && (
                    <div className="flex items-center gap-1 mt-2 pt-2 border-t text-xs text-green-600">
                      <ExternalLink className="w-3 h-3" />
                      <span>Click to navigate</span>
                    </div>
                  )}
                </div>
              </Popup>
            )}

            {/* Pin Properties Panel — inside MapGL for correct stacking context */}
            {selectedPin && interaction.mode !== 'functional' && (
              <PinPropertiesPanel
                pin={selectedPin}
                groups={groups}
                pages={pagesList}
                onUpdatePin={handleUpdatePin}
                onDeletePin={handleDeletePin}
                onRequestDataBinding={handleRequestPinDataBinding}
                onClose={handlePinPropertiesClose}
                isRepositionMode={isRepositionMode}
                onToggleRepositionMode={() => setIsRepositionMode(prev => !prev)}
              />
            )}
          </MapGL>
        )}

        {/* Legend + Layer Controls (single panel) */}
        {showLegend && mapLoaded && !loading && !error && (
          <MapLegend
            colorTheme={colorTheme}
            sensorCount={sensorData.length}
            lastUpdated={lastUpdated}
            connectionStatus={connectionStatus}
            isRealTimeEnabled={isRealTimeEnabled}
            recentlyUpdatedCount={recentlyUpdated.size}
            config={legendConfig}
            containerWidth={containerWidth}
            containerHeight={containerHeight}
            groups={groups}
            visibleGroups={effectiveVisibleGroups}
            onGroupVisibilityChange={handleGroupVisibilityChange}
            onToggleAll={handleToggleAllGroups}
            renderMode={activeRenderMode}
            onRenderModeChange={handleRenderModeChange}
            zonesEnabled={zonesEnabled}
            onZonesToggle={handleZonesToggle}
          />
        )}

        {/* Phase 4: Pin Placement Overlay */}
        <PinPlacementOverlay
          isActive={isPlacementMode}
          pendingPin={activePendingPin ?? undefined}
          cursorPosition={effectiveCursorPosition}
          onConfirm={confirmPlacement}
          onCancel={exitPlacementMode}
          onUpdatePin={updatePendingPin}
          mapContainerRef={mapContainerRef}
        />

      </div>
    </div>
  );
};

// Export as default as well
export default MapComponent;

/**
 * @deprecated Use MapComponent instead
 */
export const SoilMoistureMap = MapComponent;
